import {
  PresentationBundleKind,
  UpdatePresentationAssetDistributionBundleConfigurationInput,
} from '@/types/schema';

import { BundleSaveDescriptor } from '../presentationSave.types';
import { createGeneratedBundleCardDescriptor } from './generatedBundleCardDescriptor';

/**
 * The fifth and last kind shaped as a title, cards, and items inside each card.
 *
 * It reuses the same nesting as the grantor-card kinds — a card is a distribution
 * item, and its items are that card's detail rows — and differs in carrying two
 * more configuration scalars: the death-order eyebrow above the title, and the
 * prose description on the first page.
 *
 * Both of those are `string` on the form and never null: the comments on the type
 * say they are persisted as the empty string when unset, so clearing one writes
 * `''` rather than a clear flag. The title beside them *is* nullable, which is why
 * it still goes through the factory's own handling.
 */
export const assetDistributionBundleDescriptor: BundleSaveDescriptor =
  createGeneratedBundleCardDescriptor({
    kind: PresentationBundleKind.AssetDistributionBundle,
    field: 'assetDistributionBundleConfiguration',

    read: (bundle) => {
      const config = bundle.assetDistributionBundleConfiguration;
      if (!config) return undefined;
      return {
        id: config.id,
        displayName: config.displayName,
        itemsPerPage: config.itemsPerPage,
        cards: config.items.map((item) => ({
          id: item.id,
          name: item.displayName,
          items: item.itemDetails,
        })),
      };
    },

    diffScalars: (previous, next) => {
      const before = previous.assetDistributionBundleConfiguration;
      const after = next.assetDistributionBundleConfiguration;
      if (!before || !after) return null;

      const scalars: Pick<
        UpdatePresentationAssetDistributionBundleConfigurationInput,
        'deathOrderDisplay' | 'description'
      > = {};
      if (before.deathOrderDisplay !== after.deathOrderDisplay) {
        scalars.deathOrderDisplay = after.deathOrderDisplay;
      }
      if (before.description !== after.description) {
        scalars.description = after.description;
      }
      return Object.keys(scalars).length > 0 ? scalars : null;
    },
  });
